import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

// API base URL with production fallback
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setMessage('Invalid unsubscribe link. Please log in to manage your preferences.')
      return
    }

    const unsubscribe = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/email-preferences/unsubscribe?token=${encodeURIComponent(token)}`,
          { method: 'POST' },
        )
        const data = await response.json()

        if (response.ok && data.success) {
          setStatus('success')
          setMessage(data.message)
        } else {
          setStatus('error')
          setMessage(data.detail || 'Failed to unsubscribe. The link may have expired.')
        }
      } catch {
        setStatus('error')
        setMessage('An error occurred. Please try again or log in to manage preferences.')
      }
    }

    unsubscribe()
  }, [token])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
                <CardTitle>Processing...</CardTitle>
                <CardDescription>Please wait while we update your preferences.</CardDescription>
              </>
            )}
            {status === 'success' && (
              <>
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <CardTitle>Unsubscribed Successfully</CardTitle>
              </>
            )}
            {status === 'error' && (
              <>
                <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
                <CardTitle>Unsubscribe Failed</CardTitle>
              </>
            )}
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">{message}</p>

            {status === 'success' && (
              <p className="text-sm text-muted-foreground">
                You will still receive essential account notifications such as password resets and
                billing updates.
              </p>
            )}

            <div className="flex flex-col gap-2 pt-4">
              <Button variant="outline" onClick={() => navigate('/')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
              {status !== 'loading' && (
                <Button variant="ghost" onClick={() => navigate('/login')}>
                  Log in to manage all preferences
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
